import { useCheckEmail, useRequestCode, useVerifyCode, useCompleteSignup } from '../../api/registration/hooks';
import { useLinkFamilyDevice } from '../../api/guardian/family/hooks';
import { RegistrationApiError } from '../../api/registration/errors';
import { useResendCountdown } from '../../hooks/use-resend-countdown';
/**
 * Guardian Signup Wizard
 * Minimal account creation for guardians (responsible adults):
 *   email → verification code → name + phone → done.
 * Rendered inline by the landing page ("Create account" — no URL of its own).
 * Guardians are passwordless: they sign in with emailed 6-digit access codes.
 * On success this browser is linked as a family device and the guardian
 * lands on their dashboard.
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TurnstileWidget } from '../../components/turnstile-widget';
import { isCaptchaEnabled } from '../../components/captcha';
import type { TurnstileHandle } from '../../components/turnstile-widget';
import {
  hasFamilyDeviceToken,
  setFamilyDeviceToken,
} from '../../services/family-device-storage';
import { authService } from '../../services/authentication/auth-service';
import { VersionFooter } from '../login/version-footer';
import { FormField, FormMessage, Input } from '../../components/form-field';
import { Button } from '../../components/button';
import auth from '../login/auth.module.css';

type SignupStep = 'email' | 'code' | 'data';

/** Each step's name beside the step count: its key, written whole. */
const STEP_KEY: Record<SignupStep, string> = {
  email: 'signup.steps.email',
  code: 'signup.steps.code',
  data: 'signup.steps.data',
};

export function GuardianSignupWizard() {
  const checkEmail = useCheckEmail();
  const requestCode = useRequestCode();
  const verifyCode = useVerifyCode();
  const completeSignup = useCompleteSignup();
  const { mutateAsync: linkDevice } = useLinkFamilyDevice();
  const { t, i18n } = useTranslation();

  const [step, setStep] = useState<SignupStep>('email');
  const [email, setEmail] = useState('');
  const [emailExists, setEmailExists] = useState(false);
  const [code, setCode] = useState('');
  const [signupToken, setSignupToken] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Captcha protects the email-sending steps (send code + resend). Tokens are
  // single-use, so the widget is reset after every request. Only enforced
  // when a Turnstile sitekey is configured.
  const captchaEnabled = isCaptchaEnabled();
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<TurnstileHandle>(null);

  // Resend cooldown countdown (seconds remaining)
  const [resendAt, setResendAt] = useState<Date | null>(null);
  const resendIn = useResendCountdown(resendAt);

  /** Step 1 — check the email; unknown → send code; known → offer login */
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setEmailExists(false);
    try {
      const { exists } = await checkEmail(email);
      if (exists) {
        setEmailExists(true);
        return;
      }
      const result = await requestCode(email, i18n.language, captchaToken ?? undefined);
      setResendAt(new Date(result.resendAvailableAt));
      setStep('code');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('signup.errors.generic'));
    } finally {
      // Single-use token: refresh so the resend (or a retry) gets a new one
      captchaRef.current?.reset();
      setBusy(false);
    }
  };

  const handleResendCode = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await requestCode(email, i18n.language, captchaToken ?? undefined);
      setResendAt(new Date(result.resendAvailableAt));
    } catch (err) {
      if (err instanceof RegistrationApiError && err.status === 429) {
        setError(t('signup.errors.tooManyCodes'));
      } else {
        setError(err instanceof Error ? err.message : t('signup.errors.generic'));
      }
    } finally {
      captchaRef.current?.reset();
      setBusy(false);
    }
  };

  /** Step 2 — verify the emailed code */
  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { signupToken: token } = await verifyCode(email, code);
      setSignupToken(token);
      setStep('data');
    } catch {
      setError(t('signup.errors.invalidCode'));
    } finally {
      setBusy(false);
    }
  };

  /** Step 3 — name + phone + PIN → create the account and auto-login */
  const handleDataSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const response = await completeSignup({
        signupToken,
        firstName,
        lastName,
        phoneNumber,
      });
      authService.adoptLoginResponse(response);

      // Link this browser as a family device so the profile picker shows up
      // at the root right away; failures here shouldn't block account creation.
      try {
        if (!hasFamilyDeviceToken()) {
          const deviceName = t('guardian.devices.autoName', { name: firstName });
          const result = await linkDevice(deviceName);
          setFamilyDeviceToken(result.deviceToken);
        }
      } catch {
        // Non-fatal — the guardian can link the device from the dashboard
      }

      // Drop the ?signup param; the authenticated guardian lands on the dashboard
      window.location.href = window.location.pathname;
    } catch (err) {
      if (err instanceof RegistrationApiError && err.status === 401) {
        setError(t('signup.errors.expiredToken'));
      } else if (err instanceof RegistrationApiError && err.status === 409) {
        setError(t('signup.errors.emailTaken'));
      } else {
        setError(err instanceof Error ? err.message : t('signup.errors.generic'));
      }
      setBusy(false);
    }
  };

  const stepIndex = ['email', 'code', 'data'].indexOf(step) + 1;

  return (
    // student-portal + family-auth: the student design system, restyled by the
    // `:global(.family-auth)` variants beside each rule (auth.module.css and its kin)
    <div className={`${auth.loginContainer} portal-shell student-portal family-auth`}>

      <div className={auth.loginContent}>
        <div className={`${auth.loginCard} framed`}>
          <div className={auth.loginHeader}>
            <h1 className={auth.loginTitle}>{t('signup.title')}</h1>
            <p className={auth.loginSubtitle}>
              {t('signup.stepIndicator', { current: stepIndex, total: 3 })} — {t(STEP_KEY[step])}
            </p>
          </div>

          {step === 'email' && (
            <form onSubmit={handleEmailSubmit} className={auth.loginForm}>
              <p className={auth.signupHint}>{t('signup.emailHint')}</p>
              <FormField label={t('login.email')} required>
                {(field) => (
                  <Input {...field}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('login.enterEmail')}
                    required
                    disabled={busy}
                    autoComplete="email"
                  />
                )}
              </FormField>
              {emailExists && (
                <div className={auth.signupExistsBox}>
                  {t('signup.emailExists')}{' '}
                  <a href={window.location.pathname} className={auth.loginSignupLink}>{t('signup.goToLogin')}</a>
                </div>
              )}
              <TurnstileWidget ref={captchaRef} onToken={setCaptchaToken} />
              {error && <FormMessage>{error}</FormMessage>}
              <Button size="lg" block
                type="submit"
                disabled={!email || busy || (captchaEnabled && !captchaToken)}
              >
                {busy ? t('common.loading') : t('signup.sendCode')}
              </Button>
            </form>
          )}

          {step === 'code' && (
            <form onSubmit={handleCodeSubmit} className={auth.loginForm}>
              <p className={auth.signupHint}>{t('signup.codeHint', { email })}</p>
              <FormField label={t('signup.codeLabel')} required>
                {(field) => (
                  <Input {...field}
                    type="text"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} className={auth.signupCodeInput}
                    placeholder="000000"
                    required
                    disabled={busy}
                    autoComplete="one-time-code"
                  />
                )}
              </FormField>
              <TurnstileWidget ref={captchaRef} onToken={setCaptchaToken} />
              {error && <FormMessage>{error}</FormMessage>}
              <Button size="lg" block type="submit" disabled={code.length !== 6 || busy}>
                {busy ? t('common.loading') : t('signup.verifyCode')}
              </Button>
              <button
                type="button"
                className={auth.signupResend}
                onClick={() => void handleResendCode()}
                disabled={busy || resendIn > 0 || (captchaEnabled && !captchaToken)}
              >
                {resendIn > 0
                  ? t('signup.resendIn', { seconds: resendIn })
                  : t('signup.resendCode')}
              </button>
            </form>
          )}

          {step === 'data' && (
            <form onSubmit={(e) => void handleDataSubmit(e)} className={auth.loginForm}>
              <p className={auth.signupHint}>{t('signup.dataHint')}</p>
              <div className={auth.signupRow}>
                <FormField label={t('signup.firstName')} required>
                  {(field) => (
                    <Input {...field}
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      disabled={busy}
                      autoComplete="given-name"
                    />
                  )}
                </FormField>
                <FormField label={t('signup.lastName')} required>
                  {(field) => (
                    <Input {...field}
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      disabled={busy}
                      autoComplete="family-name"
                    />
                  )}
                </FormField>
              </div>

              <FormField label={t('signup.phone')} required>
                {(field) => (
                  <Input {...field}
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    required
                    disabled={busy}
                    autoComplete="tel"
                  />
                )}
              </FormField>

              {error && <FormMessage>{error}</FormMessage>}
              <Button size="lg" block
                type="submit"
                disabled={!firstName || !lastName || !phoneNumber || busy}
              >
                {busy ? t('common.loading') : t('signup.createAccountButton')}
              </Button>
            </form>
          )}

          <p className={auth.loginSignupHint}>
            <a href={window.location.pathname} className={auth.loginSignupLink}>
              {t('signup.backToHome')}
            </a>
          </p>
        </div>

        <div className={auth.loginVersionBox}>
          <VersionFooter />
        </div>
      </div>
    </div>
  );
}
